import { useState, FormEvent } from 'react'
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaLinkedin, FaGithub, FaTwitter } from 'react-icons/fa'
import { motion } from 'framer-motion'
import { contactData } from '../../data'
import { FadeIn, SlideIn } from '../animations/ScrollAnimations'
import emailjs from '@emailjs/browser'
import { EMAILJS_CONFIG, EmailTemplateParams } from '../../lib/emailjs'

interface FormData {
  name: string
  email: string
  company: string
  message: string
}

export default function Contact(): JSX.Element {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    message: ''
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    try {
      // Prepare template parameters for EmailJS
      const now = new Date()
      const templateParams: EmailTemplateParams = {
        from_name: formData.name,
        from_email: formData.email,
        company: formData.company || 'Not specified',
        message: formData.message,
        to_email: contactData.contactInfo.details.email.value,
        sent_date: now.toLocaleDateString(),
        sent_time: now.toLocaleTimeString()
      }

      // Send email using EmailJS
      const result = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams,
        EMAILJS_CONFIG.PUBLIC_KEY
      )

      console.log('Email sent successfully:', result)
      
      // Show success message (you can add a toast notification here)
      alert('Message sent successfully! I\'ll get back to you soon.')
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        message: ''
      })
    } catch (error) {
      console.error('Failed to send email:', error)
      alert('Failed to send message. Please try again or contact me directly via email.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  return (
    <div className="section bg-portfolio-black">
      <div className="container">
        <div className="text-center mb-16">
          <FadeIn direction="up" delay={0.2}>
            <motion.h2 
              className="heading-2"
              whileHover={{ scale: 1.02 }}
            >
              {contactData.title} <span className="gradient-text">{contactData.subtitle}</span>
            </motion.h2>
            <motion.p 
              className="text-large max-w-3xl mx-auto"
              whileHover={{ scale: 1.01 }}
            >
              {contactData.description}
            </motion.p>
          </FadeIn>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 overflow-hidden ">
          {/* Contact Form */}
          <SlideIn direction="right" delay={0.3}>
            <motion.div 
              className="card"
              whileHover={{ 
                scale: 1.01,
                boxShadow: "0 20px 40px rgba(149, 255, 49, 0.1)"
              }}
            >
            <h3 className="heading-3 mb-6">{contactData.form.title}</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  {contactData.form.fields.name.label}
                </label>
                <motion.input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent text-white placeholder-gray-400"
                  placeholder={contactData.form.fields.name.placeholder}
                  required={contactData.form.fields.name.required}
                  whileFocus={{ scale: 1.01, borderColor: "#95FF31" }}
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  {contactData.form.fields.email.label}
                </label>
                <motion.input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent text-white placeholder-gray-400"
                  placeholder={contactData.form.fields.email.placeholder}
                  required={contactData.form.fields.email.required}
                  whileFocus={{ scale: 1.01, borderColor: "#95FF31" }}
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                  {contactData.form.fields.company.label}
                </label>
                <motion.input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent text-white placeholder-gray-400"
                  placeholder={contactData.form.fields.company.placeholder}
                  required={contactData.form.fields.company.required}
                  whileFocus={{ scale: 1.01, borderColor: "#95FF31" }}
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                  {contactData.form.fields.message.label}
                </label>
                <motion.textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent text-white placeholder-gray-400 resize-none"
                  placeholder={contactData.form.fields.message.placeholder}
                  required={contactData.form.fields.message.required}
                  whileFocus={{ scale: 1.01, borderColor: "#95FF31" }}
                />
              </motion.div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                {isSubmitting ? contactData.form.button.loadingText : contactData.form.button.text}
              </motion.button>
            </form>
            </motion.div>
          </SlideIn>

          {/* Contact Information */}
          <SlideIn direction="left" delay={0.4}>
            <motion.div 
              className="space-y-8"
              whileHover={{ 
                scale: 1.01,
                boxShadow: "0 20px 40px rgba(149, 255, 49, 0.1)"
              }}
            >
            <div>
              <h3 className="heading-3 mb-6">{contactData.contactInfo.title}</h3>
              <p className="text-medium mb-8">
                {contactData.contactInfo.description}
              </p>
            </div>

            <div className="space-y-6">
              <motion.div 
                className="flex items-center gap-4"
                whileHover={{ x: 10 }}
              >
                <motion.div 
                  className="w-12 h-12 bg-green-400/20 rounded-lg flex items-center justify-center"
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                >
                  <FaEnvelope className="text-green-400 text-xl" />
                </motion.div>
                <div>
                  <motion.h4 
                    className="text-white font-semibold"
                    whileHover={{ color: "#95FF31" }}
                  >
                    {contactData.contactInfo.details.email.label}
                  </motion.h4>
                  <motion.a 
                    href={contactData.contactInfo.details.email.href} 
                    className="text-gray-400 hover:text-green-400 transition-colors"
                    whileHover={{ scale: 1.05, x: 5 }}
                  >
                    {contactData.contactInfo.details.email.value}
                  </motion.a>
                </div>
              </motion.div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-400/20 rounded-lg flex items-center justify-center">
                  <FaPhone className="text-green-400 text-xl" />
                </div>
                <div>
                  <h4 className="text-white font-semibold">{contactData.contactInfo.details.phone.label}</h4>
                  <a href={contactData.contactInfo.details.phone.href} className="text-gray-400 hover:text-green-400 transition-colors">
                    {contactData.contactInfo.details.phone.value}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-400/20 rounded-lg flex items-center justify-center">
                  <FaMapMarkerAlt className="text-green-400 text-xl" />
                </div>
                <div>
                  <h4 className="text-white font-semibold">{contactData.contactInfo.details.location.label}</h4>
                  <p className="text-gray-400">{contactData.contactInfo.details.location.value}</p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">{contactData.socialLinks.title}</h4>
              <div className="flex space-x-4">
                <a
                  href={contactData.socialLinks.links.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-green-400 hover:bg-gray-700 transition-colors"
                >
                  <FaLinkedin className="text-xl" />
                </a>
                <a
                  href={contactData.socialLinks.links.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-green-400 hover:bg-gray-700 transition-colors"
                >
                  <FaGithub className="text-xl" />
                </a>
                <a
                  href={contactData.socialLinks.links.x}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-green-400 hover:bg-gray-700 transition-colors"
                >
                  <FaTwitter className="text-xl" />
                </a>
              </div>
            </div>

            {/* Availability */}
            <div className="card">
              <h4 className="text-white font-semibold mb-2">{contactData.availability.title}</h4>
              <p className="text-gray-400 mb-4">
                {contactData.availability.description}
              </p>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                <span className="text-green-400 font-medium">{contactData.availability.status}</span>
              </div>
            </div>
            </motion.div>
          </SlideIn>
        </div>
      </div>
    </div>
  )
} 